/**
 * Super Admin Panel
 * - Create new dealers
 * - View all dealers
 * - Impersonate dealer accounts
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Plus, Eye, Loader2, Building, Mail, Calendar, 
  UserPlus, Shield, ExternalLink, Copy, Check, Sparkles, RefreshCw, ChevronRight, KeyRound, Trash2, MessageCircle, TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getAuthToken, storeAuthToken } from '../../utils/auth';
import BrokenLinkResender from './BrokenLinkResender';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = ({ isDark }) => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', business_name: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [resetResult, setResetResult] = useState(null); // { email, temp_password, emailed }
  const [copiedTempPw, setCopiedTempPw] = useState(false);
  const [runningBackfill, setRunningBackfill] = useState(false);
  const [deletingDealer, setDeletingDealer] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [newDealerCredentials, setNewDealerCredentials] = useState(null);
  const [contentStatus, setContentStatus] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);

  useEffect(() => {
    fetchDealers();
    fetchContentStatus();
  }, []);

  const fetchDealers = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/dealers/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDealers(res.data.dealers);
    } catch (err) {
      toast.error('Failed to load dealers');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentStatus = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/content-generation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContentStatus(res.data);
    } catch (err) {
      console.error('Failed to load content status:', err);
    }
  };

  const triggerContentGeneration = async () => {
    setGeneratingContent(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/admin/trigger-content-generation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content generation triggered! 3 new pieces will be created.');
      setTimeout(fetchContentStatus, 5000); // Refresh status after 5 seconds
    } catch (err) {
      toast.error('Failed to trigger content generation');
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleCreateDealer = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.business_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/admin/dealers/create`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dealer created successfully!');
      setNewDealerCredentials(res.data.dealer);
      fetchDealers();
      setCreateForm({ email: '', business_name: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create dealer');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (dealer) => {
    const nextStatus = dealer.subscription_status === 'active' ? 'inactive' : 'active';
    const verb = nextStatus === 'active' ? 'Activate' : 'Deactivate';
    if (!window.confirm(`${verb} ${dealer.business_name || dealer.email}?`)) return;
    setTogglingStatus(dealer.id);
    try {
      const token = getAuthToken();
      await axios.patch(
        `${API}/admin/dealers/${dealer.id}/subscription`,
        null,
        {
          params: { status: nextStatus },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`${dealer.business_name || dealer.email} is now ${nextStatus}`);
      setDealers((prev) => prev.map((d) =>
        d.id === dealer.id ? { ...d, subscription_status: nextStatus } : d
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleImpersonate = async (dealerId) => {
    setImpersonating(dealerId);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/dealer/${dealerId}/impersonate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store the impersonation token temporarily
      const impersonateToken = res.data.token;
      
      // Open the dealer dashboard in a new tab with the token
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head><title>Loading ${res.data.dealer.business_name} Dashboard...</title></head>
            <body>
              <script>
                localStorage.setItem('token', '${impersonateToken}');
                localStorage.setItem('impersonating', 'true');
                window.location.href = '/dealer';
              </script>
              <p>Redirecting to advisor dashboard...</p>
            </body>
          </html>
        `);
      }
      
      toast.success(`Impersonating ${res.data.dealer.business_name}`);
    } catch (err) {
      toast.error('Failed to impersonate dealer');
    } finally {
      setImpersonating(null);
    }
  };

  // Super-admin password reset — generates a temp password + emails the
  // dealer + returns the temp so the admin can share it manually (in case
  // the dealer's email is down). The dealer is force-flagged to change on
  // next login.
  const handleResetPassword = async (dealer) => {
    if (!window.confirm(`Reset password for ${dealer.email}?\n\nA temporary password will be generated, emailed to the dealer, and they'll be forced to change it on next login.`)) return;
    setResettingPassword(dealer.id);
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/admin/dealers/${dealer.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetResult({
        email: res.data.email,
        temp_password: res.data.temp_password,
        emailed: !!res.data.emailed,
        business_name: dealer.business_name,
      });
      toast.success('Temp password generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(null);
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      setCopiedTempPw(true);
      setTimeout(() => setCopiedTempPw(false), 2000);
    } catch { /* ignore */ }
  };

  const handleRunBackfill = async () => {
    if (!window.confirm('Run legacy lead backfill?\n\nThis fills in missing RV Type + length_max on older form-submitted leads across all dealers. Safe to run any time.')) return;
    setRunningBackfill(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/admin/backfill-rv-type`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data || {};
      toast.success(
        `Backfill complete: checked ${d.checked ?? 0}, ` +
        `rv_type updated ${d.updated ?? 0}, ` +
        `length_max updated ${d.length_max_updated ?? 0}`,
        { duration: 8000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunningBackfill(false);
    }
  };

  // Hard-delete a dealer. Leads keep their data but get detached so they
  // resurface as unassigned instead of getting nuked with the dealer.
  const handleDeleteDealer = async (dealer) => {
    const confirmText = `DELETE ${dealer.business_name || dealer.email}?\n\nThis removes:\n- The dealer account\n- Their user login\n- All email settings, inbox threads, cloaked links, Google tokens\n\nLeads stay in the database but get detached from this dealer.\n\nType DELETE to confirm:`;
    const answer = window.prompt(confirmText);
    if (answer !== 'DELETE') return;
    setDeletingDealer(dealer.id);
    try {
      const token = getAuthToken();
      const res = await axios.delete(
        `${API}/admin/dealers/${dealer.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Dealer deleted — ${res.data?.leads_detached ?? 0} leads detached`,
        { duration: 6000 }
      );
      // Refresh the dealer list
      await fetchDealers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingDealer(null);
    }
  };


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Share Tools card — quick link to /dealer/share-tools (super-admin only) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <a
          href="/admin/support"
          className={isDark ? 'block rounded-xl p-4 bg-gradient-to-r from-blue-900/30 to-cyan-500/10 border border-cyan-500/30 hover:from-blue-900/40 hover:to-cyan-500/20 transition-colors' : 'block rounded-xl p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-cyan-300 hover:from-blue-100 hover:to-cyan-100 transition-colors'}
          data-testid="admin-support-card"
        >
          <div className="flex items-center gap-3">
            <div className={isDark ? 'w-11 h-11 rounded-xl bg-cyan-500/20 flex items-center justify-center' : 'w-11 h-11 rounded-xl bg-cyan-100 flex items-center justify-center'}>
              <MessageCircle size={20} className="text-cyan-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-blue-900'}`}>Advisor Support Chat</h3>
              <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Live 1:1 chat with advisors</p>
            </div>
            <ChevronRight size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          </div>
        </a>
        <a
          href="/admin/dealer-emails"
          className={isDark ? 'block rounded-xl p-4 bg-gradient-to-r from-blue-900/30 to-emerald-500/10 border border-emerald-500/30 hover:from-blue-900/40 hover:to-emerald-500/20 transition-colors' : 'block rounded-xl p-4 bg-gradient-to-r from-blue-50 to-emerald-50 border border-emerald-300 hover:from-blue-100 hover:to-emerald-100 transition-colors'}
          data-testid="admin-email-card"
        >
          <div className="flex items-center gap-3">
            <div className={isDark ? 'w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center' : 'w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center'}>
              <Mail size={20} className="text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-blue-900'}`}>Email Advisors</h3>
              <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Send + track advisor emails</p>
            </div>
            <ChevronRight size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          </div>
        </a>
      <a
        href="/dealer/share-tools"
        className={isDark ? 'block rounded-xl p-4 bg-gradient-to-r from-blue-900/30 to-amber-500/10 border border-amber-500/30 hover:from-blue-900/40 hover:to-amber-500/20 transition-colors' : 'block rounded-xl p-4 bg-gradient-to-r from-blue-50 to-amber-50 border border-amber-300 hover:from-blue-100 hover:to-amber-100 transition-colors'}
        data-testid="admin-share-tools-card"
      >
        <div className="flex items-center gap-3">
          <div className={isDark ? 'w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center' : 'w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center'}>
            <Sparkles size={20} className="text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-blue-900'}`}>Share Tool Links</h3>
            <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Public share URLs for the tools</p>
          </div>
          <ChevronRight size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
        </div>
      </a>
      <a
        href="/admin?tab=rigready"
        className={isDark ? 'block rounded-xl p-4 bg-gradient-to-r from-blue-900/30 to-green-500/10 border border-green-500/30 hover:from-blue-900/40 hover:to-green-500/20 transition-colors' : 'block rounded-xl p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-green-300 hover:from-blue-100 hover:to-green-100 transition-colors'}
        data-testid="admin-rigready-funnel-card"
      >
        <div className="flex items-center gap-3">
          <div className={isDark ? 'w-11 h-11 rounded-xl bg-green-500/20 flex items-center justify-center' : 'w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center'}>
            <TrendingUp size={20} className="text-green-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-blue-900'}`}>Rig Ready Funnel</h3>
            <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>TowIQ traffic, conversions & ad sources</p>
          </div>
          <ChevronRight size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
        </div>
      </a>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 sm:p-3 rounded-xl bg-red-500/20">
            <Shield className="text-red-500" size={24} />
          </div>
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Super Admin Panel
            </h1>
            <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage dealers and platform access
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            onClick={handleRunBackfill}
            disabled={runningBackfill}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="run-backfill-btn"
            title="Fixes RV Type + length_max on legacy form-submitted leads. Safe to run any time."
          >
            {runningBackfill ? (
              <Loader2 size={16} className="animate-spin mr-1.5" />
            ) : (
              <RefreshCw size={16} className="mr-1.5" />
            )}
            Backfill Legacy Leads
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
            <Plus size={16} className="mr-1.5" />
            Create Dealer
          </Button>
        </div>
      </div>

      {/* New Dealer Credentials Alert */}
      {newDealerCredentials && (
        <div className={`p-4 rounded-xl border-2 border-green-500 ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
          <h3 className="font-bold text-green-600 mb-2 flex items-center gap-2">
            <Check size={20} />
            New Dealer Created!
          </h3>
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Share these credentials with the new dealer:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Email</Label>
              <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'} font-mono text-sm`}>
                {newDealerCredentials.email}
              </div>
            </div>
            <div>
              <Label className="text-xs">Temporary Password</Label>
              <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'} font-mono text-sm flex items-center justify-between`}>
                {newDealerCredentials.temp_password}
                <button 
                  onClick={() => copyToClipboard(newDealerCredentials.temp_password)}
                  className="text-blue-500 hover:text-blue-600"
                >
                  {copiedPassword ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Form Link</Label>
              <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'} font-mono text-xs truncate`}>
                /quiz/{newDealerCredentials.slug}
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-3"
            onClick={() => setNewDealerCredentials(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* AI Content Generation Section */}
      <div className={`rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm overflow-hidden`}>
        <div className={`px-4 sm:px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sparkles className="text-purple-500 shrink-0" size={22} />
              <div>
                <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  AI Content Engine
                </h2>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Auto-generates 3 marketing pieces daily
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchContentStatus}
                className="flex items-center gap-1.5"
              >
                <RefreshCw size={14} />
                <span className="hidden xs:inline">Refresh</span>
              </Button>
              <Button 
                onClick={triggerContentGeneration}
                disabled={generatingContent}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 flex-1 sm:flex-none"
              >
                {generatingContent ? (
                  <><Loader2 className="animate-spin mr-1.5" size={14} />Generating</>
                ) : (
                  <><Sparkles size={14} className="mr-1.5" />Generate</>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-50'}`}>
              <p className={`text-xs uppercase font-medium ${isDark ? 'text-gray-400' : 'text-purple-600'}`}>
                Total AI Content
              </p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-purple-700'}`}>
                {contentStatus?.total_ai_content || 0}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-50'}`}>
              <p className={`text-xs uppercase font-medium ${isDark ? 'text-gray-400' : 'text-green-600'}`}>
                Scheduler Status
              </p>
              <p className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                {contentStatus?.scheduler || 'Active'} ✓
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className={`text-xs uppercase font-medium ${isDark ? 'text-gray-400' : 'text-blue-600'}`}>
                Last Generated
              </p>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-blue-700'}`}>
                {contentStatus?.last_generation 
                  ? new Date(contentStatus.last_generation).toLocaleString() 
                  : 'Never'}
              </p>
            </div>
          </div>

          {contentStatus?.recent_content?.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Recent AI Content
              </h3>
              <div className="space-y-2">
                {contentStatus.recent_content.slice(0, 5).map((item, idx) => (
                  <div 
                    key={idx}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`px-2 py-1 rounded text-[10px] sm:text-xs font-medium whitespace-nowrap ${
                        item.category === 'social_post' ? 'bg-green-100 text-green-700' :
                        item.category === 'email_campaign' ? 'bg-amber-100 text-amber-700' :
                        item.category === 'blog_article' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {item.category?.replace('_', ' ')}
                      </span>
                      <span className={`text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {item.title}
                      </span>
                    </div>
                    <span className={`text-xs self-end sm:self-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Broken email-link resender */}
      <BrokenLinkResender isDark={isDark} />

      {/* Advisors Table */}
      <div className={`rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm overflow-hidden`}>
        <div className={`px-4 sm:px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            All Dealers ({dealers.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="animate-spin mx-auto mb-2" size={32} />
            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Loading advisors...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Business
                  </th>
                  <th className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Email
                  </th>
                  <th className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Status
                  </th>
                  <th className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Leads
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Created
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {dealers.map((dealer) => (
                  <tr key={dealer.id} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-blue-100'}`}>
                          <Building className={isDark ? 'text-blue-400' : 'text-blue-600'} size={20} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {dealer.business_name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            /{dealer.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {dealer.email}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        dealer.subscription_status === 'active' 
                          ? (isDark ? 'bg-green-500/20 text-green-400 border border-green-500/40' : 'bg-green-100 text-green-700')
                          : (isDark ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' : 'bg-gray-100 text-gray-600')
                      }`}>
                        {dealer.subscription_status || 'pending'}
                      </span>
                      {dealer.is_free_account && (
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs ${isDark ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' : 'bg-purple-100 text-purple-700'}`}>
                          Free
                        </span>
                      )}
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {dealer.lead_count || 0}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {dealer.created_at ? new Date(dealer.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => handleToggleActive(dealer)}
                          disabled={togglingStatus === dealer.id}
                          className={`text-xs h-8 ${
                            dealer.subscription_status === 'active'
                              ? 'bg-slate-600 hover:bg-slate-700 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                          data-testid={`toggle-active-${dealer.id}`}
                          title={dealer.subscription_status === 'active' ? 'Deactivate this dealer' : 'Activate — grant full dashboard access'}
                        >
                          {togglingStatus === dealer.id ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : dealer.subscription_status === 'active' ? (
                            'Deactivate'
                          ) : (
                            'Activate'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleImpersonate(dealer.id)}
                          disabled={impersonating === dealer.id}
                          className={`text-xs h-8 ${
                            isDark
                              ? 'bg-slate-800 hover:bg-slate-700 border-slate-600 text-slate-100'
                              : ''
                          }`}
                          data-testid={`impersonate-${dealer.id}`}
                        >
                          {impersonating === dealer.id ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <>
                              <Eye size={14} className="mr-1" />
                              Access Dashboard
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetPassword(dealer)}
                          disabled={resettingPassword === dealer.id}
                          className={`text-xs h-8 ${
                            isDark
                              ? 'bg-amber-900/30 hover:bg-amber-900/50 border-amber-700 text-amber-200'
                              : 'bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-800'
                          }`}
                          data-testid={`reset-password-${dealer.id}`}
                          title="Generate a temp password and email it to this dealer. They'll be forced to change it on next login."
                        >
                          {resettingPassword === dealer.id ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <>
                              <KeyRound size={14} className="mr-1" />
                              Reset Password
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteDealer(dealer)}
                          disabled={deletingDealer === dealer.id}
                          className={`text-xs h-8 ${
                            isDark
                              ? 'bg-red-900/30 hover:bg-red-900/50 border-red-700 text-red-200'
                              : 'bg-red-50 hover:bg-red-100 border-red-300 text-red-700'
                          }`}
                          data-testid={`delete-dealer-${dealer.id}`}
                          title="Permanently delete this dealer account. Leads are kept but detached."
                        >
                          {deletingDealer === dealer.id ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <>
                              <Trash2 size={14} className="mr-1" />
                              Delete
                            </>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Dealer Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              <div className="flex items-center gap-2">
                <UserPlus className="text-green-500" size={24} />
                Create New Dealer
              </div>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDealer} className="space-y-4 mt-4">
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Email *</Label>
              <Input
                type="email"
                placeholder="advisor@example.com"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                required
              />
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Business Name *</Label>
              <Input
                placeholder="RV Dealer Name"
                value={createForm.business_name}
                onChange={(e) => setCreateForm({...createForm, business_name: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                required
              />
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : ''}>Temporary Password</Label>
              <Input
                placeholder="Leave blank for default: changeme123"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                The dealer should change this after first login
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {creating ? <Loader2 className="animate-spin mr-2" size={18} /> : <Plus size={18} className="mr-2" />}
                Create Dealer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Temp Password Reveal Modal — after a super-admin reset. Shown ONCE
          so the admin can copy the temp password and share it with the
          dealer through a trusted channel (phone/text). */}
      <Dialog open={!!resetResult} onOpenChange={(open) => { if (!open) setResetResult(null); }}>
        <DialogContent className="max-w-md" data-testid="temp-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={18} className="text-amber-600" />
              Temporary Password
            </DialogTitle>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Share this password with <strong>{resetResult.business_name || resetResult.email}</strong>. They&apos;ll be forced to pick a new one on next login.
              </p>
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-4">
                <div className="flex items-center justify-between gap-3">
                  <code className="font-mono text-lg font-bold text-gray-900 dark:text-white break-all" data-testid="temp-password-value">
                    {resetResult.temp_password}
                  </code>
                  <Button
                    size="sm"
                    onClick={copyTempPassword}
                    className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="copy-temp-password-btn"
                  >
                    {copiedTempPw ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
              </div>
              <div className={`text-xs px-3 py-2 rounded ${resetResult.emailed
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'}`}
              >
                {resetResult.emailed
                  ? `✓ Emailed to ${resetResult.email}`
                  : `⚠ Email delivery failed — you'll need to share this password manually.`}
              </div>
              <p className="text-xs text-red-600 dark:text-red-400">
                This is the only time the temp password is shown. Close the modal and you won&apos;t be able to see it again.
              </p>
              <Button
                onClick={() => setResetResult(null)}
                className="w-full"
                data-testid="temp-password-close-btn"
              >
                Done — I&apos;ve saved the password
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
